// #17: Multi-Hotel Dashboard — company summary + hotel-wise cards + comparison.
// Read-only aggregation across all of a company's hotels.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const Cur = company?.currency || '₹';
const money = (n) => `${Cur}${Number(n || 0).toLocaleString('en-IN')}`;
const today = () => new Date().toISOString().slice(0, 10);

function Stat({ label, value, color }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e8eef7', textAlign: 'center' }}>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
      <Typography fontWeight={800} sx={{ color, fontSize: 22 }}>{value}</Typography>
    </Paper>
  );
}

export default function MultiHotelDashboard() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!company?.id) return;
    setLoading(true);
    fetch(`${config.API_BASE_URL}/multihotel/summary/${company.id}?date=${date}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setData(d || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals || {};
  const hotels = data?.hotels || [];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: '#fff',
          background: 'linear-gradient(120deg,#0a2540,#1d4ed8)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}>
        <ApartmentIcon sx={{ fontSize: 34 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant='h5' fontWeight={800}>
            {company?.name || 'Company'} — Multi-Hotel Dashboard
          </Typography>
          <Typography sx={{ opacity: 0.85 }}>{t.hotels || 0} hotels · {date}</Typography>
        </Box>
        <TextField type='date' size='small' label='Date' InputLabelProps={{ shrink: true }}
          value={date} onChange={(e) => setDate(e.target.value)} sx={{ bgcolor: '#fff', borderRadius: 1 }} />
        <Button variant='contained' color='inherit' startIcon={<RefreshIcon />} onClick={load}
          sx={{ color: '#0a2540', fontWeight: 700 }}>Apply</Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Company summary */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={2}><Stat label='Total Rooms' value={t.totalRooms || 0} color='#1d4ed8' /></Grid>
            <Grid item xs={6} md={2}><Stat label='Occupied' value={t.occupied || 0} color='#dc2626' /></Grid>
            <Grid item xs={6} md={2}><Stat label='Available' value={t.available || 0} color='#059669' /></Grid>
            <Grid item xs={6} md={2}><Stat label='Check-Ins' value={t.checkins || 0} color='#0891b2' /></Grid>
            <Grid item xs={6} md={2}><Stat label='Room Revenue' value={money(t.roomRevenue)} color='#7c3aed' /></Grid>
            <Grid item xs={6} md={2}><Stat label='Restaurant' value={money(t.restaurantRevenue)} color='#d97706' /></Grid>
          </Grid>

          {/* Hotel-wise cards */}
          <Grid container spacing={2} mb={3}>
            {hotels.map((h) => (
              <Grid item xs={12} sm={6} md={4} key={h.hotelId}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e8eef7' }}>
                  <Typography fontWeight={800} sx={{ color: '#0f172a', mb: 1 }}>{h.hotelName}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant='body2' color='text.secondary'>Occupancy</Typography>
                    <Typography variant='body2' fontWeight={700}>{h.occupancyPct}%</Typography>
                  </Box>
                  <LinearProgress variant='determinate' value={Math.min(100, h.occupancyPct)}
                    sx={{ height: 8, borderRadius: 2, mb: 1.5 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography variant='body2'>Rooms: <b>{h.totalRooms}</b></Typography></Grid>
                    <Grid item xs={6}><Typography variant='body2'>Available: <b>{h.available}</b></Typography></Grid>
                    <Grid item xs={6}><Typography variant='body2'>Check-in: <b>{h.checkins}</b></Typography></Grid>
                    <Grid item xs={6}><Typography variant='body2'>Check-out: <b>{h.checkouts}</b></Typography></Grid>
                    <Grid item xs={12}><Typography variant='body2' color='primary'>Revenue: <b>{money(h.todayRevenue)}</b></Typography></Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Comparison table */}
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eef7', overflow: 'hidden' }}>
            <Typography fontWeight={800} sx={{ p: 2 }}>Hotel Comparison</Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Hotel', 'Rooms', 'Occupied', 'Available', 'Occupancy', 'Check-in', 'Check-out', 'Revenue'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0a2540', color: '#fff', fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {hotels.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align='center' sx={{ py: 4, color: '#94a3b8' }}>No hotels.</TableCell></TableRow>
                ) : (
                  hotels.map((h) => (
                    <TableRow key={h.hotelId} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{h.hotelName}</TableCell>
                      <TableCell>{h.totalRooms}</TableCell>
                      <TableCell>{h.occupied}</TableCell>
                      <TableCell>{h.available}</TableCell>
                      <TableCell>{h.occupancyPct}%</TableCell>
                      <TableCell>{h.checkins}</TableCell>
                      <TableCell>{h.checkouts}</TableCell>
                      <TableCell>{money(h.todayRevenue)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
